import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Card,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Grid,
  Avatar,
  Chip,
  Modal,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import WorkIcon from "@mui/icons-material/Work";
import axios from "axios";
import baseURL from "../../config";
import { useParams } from "react-router-dom";

const VacancyCvMatcherResponsive = () => {
  const [vacancies, setVacancies] = useState([]);
  const [filteredVacancies, setFilteredVacancies] = useState([]);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [matchedCvs, setMatchedCvs] = useState([]);
  const [loadingVacancy, setLoadingVacancy] = useState(true);
  const [loadingCVs, setLoadingCVs] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [currentCv, setCurrentCv] = useState(null);
  const [status, setStatus] = useState("Approved");
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const { id } = useParams();

  // ✅ Fetch vacancies
  useEffect(() => {
    const fetchVacancies = async () => {
        const userId = localStorage.getItem("urlID");
      try {
        const res = await axios.get(`${baseURL}/api/vacancy/postedby/${userId}/active`);
        const vacList = Array.isArray(res.data) ? res.data : res.data.data || [];
        setVacancies(vacList);
        setFilteredVacancies(vacList);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingVacancy(false);
      }
    };
    fetchVacancies();
  }, []);

  // ✅ Search filter
  useEffect(() => {
    const filtered = vacancies.filter(
      (vac) =>
        vac.rank.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (vac.name && vac.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredVacancies(filtered);
  }, [searchTerm, vacancies]);

  // ✅ Fetch matched CVs
  const handleVacancyClick = async (vacancy) => {
    setSelectedVacancy(vacancy);
    setLoadingCVs(true);
    setMatchedCvs([]);
    try {
      const res = await axios.get(`${baseURL}/api/vacancy/matchedcvs/${vacancy._id}`);
      setMatchedCvs(Array.isArray(res.data) ? res.data : res.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingCVs(false);
    }
  };

  // ✅ Open modal for any action
  const handleOpenModal = (cv, action) => {
    setCurrentCv({ ...cv.cvs, crewCvId: cv._id });
    setStatus(action);
    setOpenModal(true);
  };

  // ✅ Submit Status Update
  const handleSubmitApproval = async () => {
    if (!selectedVacancy || !currentCv) return;
    setSubmitting(true);

    try {
      const urlID = localStorage.getItem("urlID");
      const userType = localStorage.getItem("userType");

      const payload = {
        vacancyId: selectedVacancy._id,
        crewCvId: currentCv.crewCvId,
        cvId: currentCv._id,
        status,
        remark,
        vesselOwnerId: userType === "vessel_owner" ? urlID : null,
        executiveId: userType === "executive" ? urlID : null,
      };

      const res = await axios.post(`${baseURL}/api/vacancyBasedCrewCVs`, payload);
      alert(res.data.message || `${status} submitted ✅`);

      setOpenModal(false);
      setRemark("");
      setStatus("Approved");
      handleVacancyClick(selectedVacancy); // refresh CV list
    } catch (err) {
      console.error("❌ Error submitting:", err);
      alert(err.response?.data?.message || "Error updating CV status");
    } finally {
      setSubmitting(false);
    }
  };

  // ✅ Get status chip
  const getStatusChip = (statusType) => {
    switch (statusType) {
      case "Approved":
        return <Chip icon={<CheckCircleIcon />} label="Approved" color="success" />;
      case "Rejected":
        return <Chip icon={<CancelIcon />} label="Rejected" color="error" />;
      case "Interviewed":
        return <Chip icon={<WorkIcon />} label="Interviewed" color="info" />;
      case "Closed":
        return <Chip icon={<CancelIcon />} label="Closed" color="default" />;
      default:
        return <Chip icon={<PendingActionsIcon />} label="Pending" color="warning" />;
    }
  };

  // ✅ Decide available actions based on current status
  const renderActions = (latestStatus, cv) => {
    switch (latestStatus) {
      case "Pending":
        return (
          <>
            <Button variant="contained" color="success" size="small" onClick={() => handleOpenModal(cv, "Approved")}>
              Approve
            </Button>
            <Button variant="contained" color="error" size="small" onClick={() => handleOpenModal(cv, "Rejected")}>
              Reject
            </Button>
          </>
        );
      case "Approved":
        return (
          <Button variant="contained" color="info" size="small" onClick={() => handleOpenModal(cv, "Interviewed")}>
            Interview
          </Button>
        );
      case "Interviewed":
        return (
          <Button variant="contained" color="secondary" size="small" onClick={() => handleOpenModal(cv, "Closed")}>
            Close
          </Button>
        );
      case "Rejected":
      case "Closed":
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        p: 2,
        gap: 2,
        bgcolor: "#fff0f0",
        minHeight: "100vh",
      }}
    >
      {/* ✅ Vacancies Panel */}
      <Box
        sx={{
          width: { xs: "100%", sm: 320 },
          borderRadius: 2,
          border: "1px solid #ddd",
          p: 2,
          bgcolor: "#fff",
          boxShadow: 2,
          overflowY: { xs: "visible", sm: "auto" },
          maxHeight: { sm: "90vh" },
        }}
      >
        <Typography variant="h6" fontWeight="bold" mb={2}>
          🔹 Vacancies
        </Typography>

        <TextField
          placeholder="Search by rank or title"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {loadingVacancy ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CircularProgress />
          </Box>
        ) : filteredVacancies.length > 0 ? (
          <List>
            {filteredVacancies.map((vac) => (
              <ListItemButton
                key={vac._id}
                selected={selectedVacancy?._id === vac._id}
                onClick={() => handleVacancyClick(vac)}
                sx={{
                  borderRadius: 2,
                  mb: 1,
                  px: 2,
                  py: 1.5,
                  background:
                    selectedVacancy?._id === vac._id
                      ? "linear-gradient(120deg, #ff6b6b, #d12c2c)"
                      : "#fff5f5",
                  color: selectedVacancy?._id === vac._id ? "#fff" : "#000",
                  "&:hover": { transform: "scale(1.02)", transition: "0.2s" },
                }}
              >
                <ListItemText
                  primary={vac.title || "Untitled"}
                  secondary={`Rank: ${vac.rank}`}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Typography>No vacancies found.</Typography>
        )}
      </Box>

      {/* ✅ CVs Panel */}
      <Box sx={{ flexGrow: 1, mt: { xs: 2, sm: 0 }, overflowY: "auto" }}>
        {selectedVacancy ? (
          <>
            <Typography variant="h6" fontWeight="bold" mb={2}>
              🔹 Matched CVs for {selectedVacancy.rank}
            </Typography>

            {loadingCVs ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
                <CircularProgress />
              </Box>
            ) : matchedCvs.length > 0 ? (
              <Grid container spacing={2}>
                {matchedCvs.map((cv) => {
                  const latestStatus =
                    cv.statusHistory?.length > 0
                      ? cv.statusHistory[cv.statusHistory.length - 1].statusType
                      : "Pending";

                  return (
                    <Grid item xs={12} sm={6} md={4} key={cv.cvs._id}>
                      <Card
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          boxShadow: 3,
                          background: "#fff",
                          textAlign: "center",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                        }}
                      >
                        {/* Profile Avatar */}
                        <Avatar
                          src={
                            cv.cvs.profilepicture?.filePath
                              ? `${baseURL}/${cv.cvs.profilepicture.filePath.replace(/\\/g, "/")}`
                              : ""
                          }
                          alt={cv.cvs.name || "Profile"}
                          sx={{ width: 70, height: 70, mb: 1, mx: "auto" }}
                        />

                        {/* Name + Rank */}
                        <Typography fontWeight="bold" fontSize={16}>
                          {cv.cvs.name}
                        </Typography>
                        <Chip
                          label={`Rank: ${cv.cvs.rank}`}
                          size="small"
                          color="primary"
                          sx={{ my: 1 }}
                        />

                        {/* Latest Status */}
                        <Box sx={{ mt: 1 }}>{getStatusChip(latestStatus)}</Box>

                        {/* Status History */}
                        {cv.statusHistory?.length > 0 && (
                          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "center" }}>
                            {cv.statusHistory.map((h, idx) => (
                              <Tooltip key={idx} title={`${h.statusType} - ${h.remark || "No remark"}`}>
                                <Chip size="small" variant="outlined" label={h.statusType} />
                              </Tooltip>
                            ))}
                          </Box>
                        )}

                        {/* Actions */}
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2, justifyContent: "center" }}>
                          {/* Download */}
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DownloadIcon />}
                            href={`${baseURL}/${cv.cvs.filePath}`}
                            target="_blank"
                            size="small"
                          >
                            Download
                          </Button>

                          {/* Action Buttons */}
                          {renderActions(latestStatus, cv)}
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography sx={{ mt: 5 }} color="text.secondary" align="center">
                No CVs matched for this vacancy.
              </Typography>
            )}
          </>
        ) : (
          <Typography sx={{ mt: 10 }} variant="h6" color="text.secondary" align="center">
            Select a vacancy to view matched CVs
          </Typography>
        )}
      </Box>

      {/* ✅ Modal */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" mb={2}>
            {status} CV
          </Typography>
          <TextField
            label="Remark (optional)"
            fullWidth
            multiline
            rows={3}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={handleSubmitApproval}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} /> : `Submit ${status}`}
          </Button>
        </Box>
      </Modal>
    </Box>
  );
};

export default VacancyCvMatcherResponsive;
